import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {UserService} from 'app/auth/service';
import {defaultTimeZone} from 'app/global/global-settings';
import {GlobalService} from 'app/global/global.service';
import {DocumentsService} from 'app/main/pages/documents/documents.service';
import {DocumentTypeService} from 'app/main/pages/settings/documenttype/document-type.service';
import {ChatScopEnum} from 'app/main/signing-pages/activity-logs/activity-form/activity-form.component';
import {manupilateInternalChat} from 'app/main/signing-pages/signing-sidebar/activity-logs/activity-helper';
import {ActivityLogService} from 'app/main/signing-pages/signing-sidebar/activity-logs/activity-logs-service';
import {catchError, map, mergeMap, of, switchMap, tap} from 'rxjs';
import {IDocumentTypesDto} from '../../../pages/documents/dto/document-listing.dto';
import * as sharedStoreActions from './shared-store.action';

@Injectable({
    providedIn: 'root'
})

export class ShareStoreEffects {


    constructor (
        private _actions$: Actions, 
        private _activityLogService$: ActivityLogService,
        private _documentService$: DocumentsService,
        private _globalService$: GlobalService,
        private _users: UserService,
        private _documentTypeService: DocumentTypeService
    ) {}




    fetchRecipientActivities$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchRecipientActivities),
            switchMap(params => {
                return this._activityLogService$.getActivityLogs(params.roleId).pipe(
                    map(response => sharedStoreActions.fetchRecipientActivitiesSuccess(response)),
                    catchError(error => of(sharedStoreActions.fetchRecipientActivitiesFailure(error)))
                )
            })
        )
    });


    fetchRecipientActivitiesFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchRecipientActivitiesFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false}) 
    
    
    fetchDocumentActivities$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchDocumentActivities),
            mergeMap(params => {
                return this._documentService$.fetchDocumentActivity(params.documentId).pipe(
                    map((response) => response.map(activity => manupilateInternalChat(activity))),
                    map(response => sharedStoreActions.fetchDocumentActivitiesSuccess(response)),
                    catchError(error => of(sharedStoreActions.fetchDocumentActivitiesFailure(error)))
                )
            })
        )
    });


    fetchDocumentActivitiesFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchDocumentActivitiesFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false});
    
    

    sendInternalMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.sendInternalMessage),
            switchMap(params => {
                return this._documentService$.sendDocumentChat(params.documentId, params.formData).pipe(
                    map((response) => {
                        if(response.scope === ChatScopEnum.Public) return response;
                        let message = response?.message;
                        for (let i = 0; i < response?.tags?.length; i++) {
                          // @ts-expect-error replaceAll does not exist on current TS setup
                            message = message.replaceAll(
                            '@{' + response.tags[i]?.user.id + '}',
                            response.tags[i]?.user.name
                          );
                        }
                        response.message = message;
                        return response;
                    }), 
                    map(response => sharedStoreActions.sendInternalMessageSuccess(response)),
                    catchError(error => of(sharedStoreActions.sendInternalMessageFailure(error)))
                )

            })
        )
    });



    sendInternalMessageFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.sendInternalMessageFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false});



    fetchSubUsers$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchSubUsers),
            switchMap(() => {
                return this._users.getSubUsers().pipe(
                    map(response => sharedStoreActions.fetchSubUsersSuccess(response)),
                    catchError(error => of(sharedStoreActions.fetchSubUsersFailure(error)))
                )
            })
        )
    });



    fetchSubUsersFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchSubUsersFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false});



    updateRecipientActivityStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.updateRecipientActivityStatus),
            switchMap(params => {
                return this._activityLogService$.updateActivityStatus(params?.roleId, {activity_ids: params.activityIds}).pipe(
                    map(() => sharedStoreActions.updateRecipientActivityStatusSuccess(params?.roleId, params.activityIds)),
                    catchError(error => of(sharedStoreActions.updateRecipientActivityStatusFailure(error)))
                )
            })
        )
    });



    updateRecipientActivityStatusFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.updateRecipientActivityStatusFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false});


    updateDocumentActivityStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.updateDocumentActivityStatus),
            switchMap(params => {
                return this._documentService$.updateDocumentActivityStatus(params?.roleId, params.activityIds).pipe(
                    map(() => sharedStoreActions.updateDocumentActivityStatusSuccess(params?.roleId, params.activityIds)),
                    catchError(error => of(sharedStoreActions.updateDocumentActivityStatusFailure(error)))
                )
            })
        )
    });



    updateDocumentActivityStatusFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.updateDocumentActivityStatusFailure),
            tap(args => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false});


    documentTypesFetch$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchDocumentTypes),
            switchMap(() =>
                this._documentTypeService.getDocumentTypes('1', '', 'all', true)
                    .pipe(
                        map(result => sharedStoreActions.fetchDocumentTypesSuccess(result as IDocumentTypesDto[] ?? [])),
                        catchError(error => of(sharedStoreActions.fetchDocumentTypesFailure(error)))
                    )
            )
        )
    });


    documentTypesFetchFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.fetchDocumentTypesFailure),
            tap(params => {
                this._globalService$.toastrError(params.error);
            })
        )
    }, {dispatch: false});



    saveUserTimeZoneSetting$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sharedStoreActions.saveUserTimeZoneSetting),
            switchMap(({user_timezone}) => {
                const userTimeZone = user_timezone ?? defaultTimeZone;
                return of(sharedStoreActions.saveUserTimeZoneSettingSuccess(userTimeZone))
            })
        )
    });


}
